import React from 'react';
import { EventKind, ObjectKind, useEventsQuery } from 'src/graphql/_generated';
import styled from 'styled-components';
import { CommentSkeleton, Poppins } from 'src/common';
import { ReactComponent as CrossIcon } from 'src/assets/cross.svg';
import { AddComment, Comment } from './comps';
import { useAuth } from 'src/state/auth';

const CloseIcon = styled(CrossIcon)`
  cursor: pointer;
  fill: ${({ theme }) => theme.colors.prussianBlue};

  &:hover {
    fill: ${({ theme }) => theme.colors.gp1};
  }
`;

const PanelContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  background-color: ${({ theme }) => theme.colors.white};
  border-left: 1px solid ${({ theme }) => theme.colors.stroke};

  .panel-body {
    &__header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      min-height: 86px;
      background-color: ${({ theme }) => theme.colors.aliceBlue};
      padding: 0 22px;
    }
    &__add-comment {
      padding: 22px;
      padding-bottom: 10px;
    }
    &__comments {
      overflow-y: auto;
      overflow-x: hidden;
      flex: 1;
      .comment:not(:last-child) {
        border-bottom: 1px solid ${({ theme }) => theme.colors.stroke};
      }
    }
  }

  ${({ theme }) => theme.breakpoints.down('md')} {
    .panel-body {
      &__header {
        min-height: 56px;
        padding: 0 18px;
      }
      &__add-comment {
        padding: 18px;
        padding-bottom: 10px;
      }
    }
  }
`;

interface CommentsProps {
  onClose: () => void;
  eventData: { objectType: ObjectKind; objectId: string };
  refetch: () => void;
}

const Comments: React.FC<CommentsProps> = ({ onClose, eventData, refetch }) => {
  const { workspaceId } = useAuth();

  const { data, loading, refetch: refetchComments } = useEventsQuery({
    variables: {
      workspaceId: workspaceId!,
      objectType: eventData.objectType,
      objectId: eventData.objectId,
      eventType: EventKind.Comment,
    },
  });

  const comments = data?.events || [];

  const handleCommentAdded = () => {
    refetchComments();
    refetch();
  };

  return (
    <PanelContainer data-cy="comments-drawer">
      <div className="panel-body__header">
        <Poppins px={20}>Comments</Poppins>
        <CloseIcon onClick={onClose} data-cy="comments-close" />
      </div>
      <div className="panel-body__add-comment">
        <AddComment eventData={eventData} onCommentAdded={handleCommentAdded} />
      </div>
      <div className="panel-body__comments">
        {loading && !data ? (
          <CommentSkeleton />
        ) : (
          comments.map((event) => (
            <Comment key={event.id} event={event} eventData={eventData} onMutate={handleCommentAdded} />
          ))
        )}
      </div>
    </PanelContainer>
  );
};

export default Comments;
